import { ChangeDetectorRef, Component, Input, OnInit, Output } from "@angular/core";
import { ApiService } from "../../../../services/api.service";
import { UnsubscribeComponent } from "../../../../generals/Components";
import { 
    AdminModuleDetail,
    LabelValue,
    LabelValueNumber,
    ModuleProductPricingConfig,
    ModuleProductPricingConfigPricingRule,
    ModuleProductPricingConfigPricingRuleCondition,
    ModuleProductPricingConfigRoundingRule,
    ModuleProductPricingConfigPricingRuleAdditionMode,
    ProductPriceType,
    ModuleProductPricingConfigRoundingRuleExample,
    ModuleProductPricingConfigRoundingRuleMethod
} from "../../../../interfaces/interfaces";
import { filter, takeUntil } from "rxjs/operators";
import { combineLatest } from "rxjs";
import { DialogService } from "primeng/dynamicdialog";
import { CategorySelectorModalComponent } from "../../../categoryselectormodal/categoryselectormodal.component";
import { randomIntFromInterval } from "../../../../helpers/helper";
import { EventEmitter } from "@angular/core";

@Component({
    selector: 'app-productpricingmodule',
    templateUrl: './productpricingmodule.component.html',
    styleUrls: ['./productpricingmodule.component.scss']
})
export class ProductPricingModuleComponent extends UnsubscribeComponent implements OnInit {

    @Input() module: AdminModuleDetail<ModuleProductPricingConfig> = null;
    sourcePriceId: number = null;
    targetPriceId: number = null;
    pricingRules: ModuleProductPricingConfigPricingRule[] = [];
    useSalePrice: boolean = false;
    salePriceToSalePrice: boolean = false;
    roundingRulesWithExamples: ModuleProductPricingConfigRoundingRuleExample[] = [];
    productPriceTypes: LabelValueNumber[] = [
        { label: 'Nincs kiválasztva', value: null }
    ];
    pricingRuleConditions: LabelValue[] = [
        { label: 'Minden termékre', value: ModuleProductPricingConfigPricingRuleCondition.ALL },
        { label: 'Kategóriákra', value: ModuleProductPricingConfigPricingRuleCondition.CATEGORIES }
    ];
    pricingRuleAdditionModes: LabelValue[] = [
        { label: 'Százalék', value: ModuleProductPricingConfigPricingRuleAdditionMode.PERCENT },
        { label: 'Pontos érték', value: ModuleProductPricingConfigPricingRuleAdditionMode.VALUE }
    ];
    roundingMethods: LabelValue[] = [
        { label: 'Kilenc nullákkal', value: ModuleProductPricingConfigRoundingRuleMethod.NINE },
        { label: 'Csak kilencesek', value: ModuleProductPricingConfigRoundingRuleMethod.NINES },
        { label: 'Csak nullák', value: ModuleProductPricingConfigRoundingRuleMethod.ZERO }
    ];
    ModuleProductPricingConfigPricingRuleCondition = ModuleProductPricingConfigPricingRuleCondition;

    @Output() inited: EventEmitter<boolean> = new EventEmitter();
    @Output() configChanged: EventEmitter<boolean> = new EventEmitter();

    constructor(
        private apiService: ApiService,
        private dialogService: DialogService,
        private cdr: ChangeDetectorRef
    ) {
        super();
    }

    ngOnInit(): void {
        this.inited.emit(true);
        this.init();
    }

    detectChanges(): void {
        this.cdr.detectChanges();
    }

    addPricingRule(): void {
        this.pricingRules.push({
            condition: ModuleProductPricingConfigPricingRuleCondition.ALL,
            additionMode: ModuleProductPricingConfigPricingRuleAdditionMode.PERCENT,
            additionValue: 0,
            value: ''
        });
        this.configChangedEvent();
    }

    deletePricingRule(index: number): void {
        if (confirm('Biztosan törlöd ezt az árazási szabályt?')) {
            this.pricingRules.splice(index, 1);
            this.configChangedEvent();
        }
    }

    addRoundingRule(): void {
        const roundingRule: ModuleProductPricingConfigRoundingRule = {
            digitCount: 0,
            method: ModuleProductPricingConfigRoundingRuleMethod.ZERO,
            min: 0,
            roundingPoint: 0
        };
        this.roundingRulesWithExamples.push({
            digitCount: roundingRule.digitCount,
            min: roundingRule.min,
            roundingPoint: roundingRule.roundingPoint,
            method: roundingRule.method,
            examples: [
                randomIntFromInterval(roundingRule.min, Math.pow(10, roundingRule.digitCount + 2)),
                randomIntFromInterval(roundingRule.min, Math.pow(10, roundingRule.digitCount + 2)),
                randomIntFromInterval(roundingRule.min, Math.pow(10, roundingRule.digitCount + 2))
            ]
        });
        this.configChangedEvent();
    }

    deleteRoundingRule(index: number): void {
        if (confirm('Biztosan törlöd ezt a kerekítési szabályt?')) {
            this.roundingRulesWithExamples.splice(index, 1);
            this.configChangedEvent();
        }
    }

    setValues(): void {
        this.sourcePriceId = this.module.config.sourcePriceId;
        this.targetPriceId = this.module.config.targetPriceId;
        this.pricingRules = this.module.config.pricingRules;
        this.useSalePrice = this.module.config.useSalePrice;
        this.salePriceToSalePrice = this.module.config.salePriceToSalePrice;
        this.roundingRulesWithExamples = this.module.config.roundingRules.map((roundingRule: ModuleProductPricingConfigRoundingRule, index: number) => ({
            ...roundingRule,
            roundingPoint: parseFloat(roundingRule.roundingPoint as string),
            examples: [
                randomIntFromInterval(roundingRule.min, this.module.config.roundingRules[index + 1]?.min || Math.pow(10, roundingRule.digitCount + 2)),
                randomIntFromInterval(roundingRule.min, this.module.config.roundingRules[index + 1]?.min || Math.pow(10, roundingRule.digitCount + 2)),
                randomIntFromInterval(roundingRule.min, this.module.config.roundingRules[index + 1]?.min || Math.pow(10, roundingRule.digitCount + 2))
            ]
        }));
    }

    init(): void {
        this.loadData();
    }

    loadData(): void {
        combineLatest([
            this.apiService.getSimpleTable('productpricetypes')
        ]).subscribe(([{data}]) => {
            this.productPriceTypes.push(...(data as ProductPriceType[]).map((productPriceType: ProductPriceType) => ({
                label: productPriceType.name,
                value: productPriceType.id
            })));
            this.setValues();
        });
    }

    configChangedEvent(): void {
        this.configChanged.emit(true);
    }

    showCategorySelectorModal(pricingRule: ModuleProductPricingConfigPricingRule): void {
        this.dialogService.open(CategorySelectorModalComponent, {
            data: {
                selectedIds: pricingRule.value.split(',').map((value: string) => parseInt(value))
            }
        }).onClose.pipe(filter(res => typeof res !== undefined)).subscribe((selectedCategoryIds: number[]) => {
            pricingRule.value = selectedCategoryIds.join(',');
            this.configChangedEvent();
        });
    }

    saveConfig(): void {
        const config: ModuleProductPricingConfig = {
            sourcePriceId: this.sourcePriceId,
            targetPriceId: this.targetPriceId,
            pricingRules: this.pricingRules,
            useSalePrice: this.useSalePrice,
            salePriceToSalePrice: this.salePriceToSalePrice,
            roundingRules: this.roundingRulesWithExamples.map((roundingRulesWithExample: ModuleProductPricingConfigRoundingRuleExample) => ({
                digitCount: roundingRulesWithExample.digitCount,
                method: roundingRulesWithExample.method,
                min: roundingRulesWithExample.min,
                roundingPoint: roundingRulesWithExample.roundingPoint
            }))
        };
        this.apiService.saveModuleConfig(this.module.id, config).pipe(takeUntil(this.unsubscribe)).subscribe(() => {
            this.configChanged.emit(false);
        });
    }

    getRoundingRuleValue(roundingRule: ModuleProductPricingConfigRoundingRuleExample): number {
        return (roundingRule.roundingPoint as number) * Math.pow(10, roundingRule.digitCount);
    }

    getDescription(roundingRule: ModuleProductPricingConfigRoundingRuleExample, index: number): string {
        let methodText: string = null;
        switch (roundingRule.method) {
            case ModuleProductPricingConfigRoundingRuleMethod.NINE:
                methodText = 'egy vezető kilences és a többi "0"';
                break;
            case ModuleProductPricingConfigRoundingRuleMethod.NINES:
                methodText = 'kilencesekkel lesz feltöltve';
                break;
            case ModuleProductPricingConfigRoundingRuleMethod.ZERO:
                methodText = 'nullákkal lesz feltöltve';
                break;
        }
        const nextMin: number | null = this.roundingRulesWithExamples[index + 1]?.min || null;
        const nextString: string = nextMin !== null ? (nextMin - 1).toString() : '∞';
        return [
            'Amennyiben az áru értéke',
            roundingRule.min,
            'és',
            nextString,
            'között van, az utolsó',
            roundingRule.digitCount,
            'számjegy fog változni, az alábbiak szerint:',
            methodText
        ].join(' ');
    }

}
